import { HEATMAP_FOLDER_ITEM_ID } from "@const/dashboard.constants";
import { CustomLinksByItemResponseDTO, CustomLinksFlatResponseDTO, ItemCustomLinksByItem, UpdateCustomLinkDTO } from "@dto/customLinks.dto";
import { Breadcrumb, BreadcrumbProps, Button, Card, Dropdown, Form, Modal, Tooltip } from "antd";
import { ItemType } from "antd/es/breadcrumb/Breadcrumb";
import { AxiosError } from "axios";
import { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { BsBookmarkStar, BsBookmarkStarFill, BsLink45Deg } from "react-icons/bs";
import { FaShare } from "react-icons/fa";
import { Link } from "react-router";
import { useAppGlobStore } from "src/appGlobalStore";
import { XmCgHeatmapFavorite } from "src/component/breadcrumb/XmCgHeatmapFavorite";
import CustomLink from "src/content/tools/cmdb/customLinks/CustomLink";
import CustomLinksFormList, { CustomLinkEntry } from "src/content/tools/cmdb/customLinks/CustomLinksFormList";
import { sortCustomLinks } from "src/content/tools/cmdb/customLinks/sortCustomLinks";
import { API_URL } from "src/data/Api";
import { RoutePath } from "src/data/Routes";
import { MenuNode } from "src/model/MenuNode";
import { Log } from "src/service/log.service";
import { getApi, putApi } from "src/util/apiCalls";
import { GLOB } from "src/util/Glob";
import './Breadcrumb.less';
import { addOrDeleteMenuFavorite } from "./menuFavoritesService";

const heatmap = 'Heatmap';
const tabHeatmap = '#tab=' + heatmap;
const tabSubHeatmap = `${tabHeatmap}&subTab=${heatmap}`;

export const BREADCRUMB_CLASS = 'xm-breadcrumb' as const;

export const XmBreadcrumb: FC<{ bread?: BreadcrumbProps; } & { isSummary?: boolean; }> = props => {
  const params = new URLSearchParams(GLOB.location?.search);
  const itemId = params.get('item_id');

  const menuFavorites = useAppGlobStore(s => s.menuFavorites);
  const addMenuFavorite = useAppGlobStore(s => s.addMenuFavorite);
  const removeMenuFavorite = useAppGlobStore(s => s.removeMenuFavorite);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);
  const [isFavorite, setIsFavorite] = useState(
    menuFavorites.some(mf => mf.url === GLOB.location?.pathname + GLOB.location?.search)
  );

  const [customLinks, setCustomLinksState] = useState<ItemCustomLinksByItem['custom_links']>(undefined);
  const setCustomLinks = (links: ItemCustomLinksByItem['custom_links']) => setCustomLinksState(sortCustomLinks(links));
  const [isLoadingCustomLinks, setIsLoadingCustomLinks] = useState(true);
  const [isCustomLinksModalOpen, setIsCustomLinksModalOpen] = useState(false);

  // when the links don't fit next to the centered title, collapse them into a dropdown
  const containerRef = useRef<HTMLDivElement>(null);
  const linksMeasureRef = useRef<HTMLDivElement>(null);
  const [areLinksCollapsed, setAreLinksCollapsed] = useState(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || customLinks?.length === 0)
      return;

    const measure = () => {
      const nav = container.querySelector<HTMLElement>('nav.ant-breadcrumb');
      const links = linksMeasureRef.current;
      if (!nav || !links)
        return;

      // the links' right edge is set by CSS, so measure from where they end to where the title ends
      const rightGap = links.getBoundingClientRect().right - nav.getBoundingClientRect().right;
      setAreLinksCollapsed(links.scrollWidth + 8 > rightGap);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [customLinks]);

  useEffect(() => {
    if (!itemId)
      return;

    const controller = new AbortController();

    (() => {
      getApi<CustomLinksByItemResponseDTO>(`${API_URL.CONFIGURATION}/custom-links/${itemId}`, controller.signal).then(
        ({ data: { data } },) => {
          if (data?.show_custom_links_config && data?.custom_links)
            setCustomLinks(data.custom_links);
        },
        (reason: AxiosError) => {
          if (reason.response?.status === 404)
            return;

          Log.error('Failed to get custom links!', reason);
        }
      ).finally(() => setIsLoadingCustomLinks(false));
    })();
  }, [itemId]);

  const addSummaryFavorite = async () => {
    setIsLoadingFavorites(true);
    try {
      const isDelete = menuFavorites.some(mf => mf.url === GLOB.location.pathname + GLOB.location.search);
      const result = await addOrDeleteMenuFavorite({
        url: GLOB.location.pathname + GLOB.location.search,
        label: MenuNode.getParentNodes(GLOB.selectedItem).map((mn) => mn.titleText || mn.title).join(' > '),
        delete: isDelete
      });
      if (isDelete) {
        removeMenuFavorite(GLOB.location.pathname + GLOB.location.search);
        setIsFavorite(false);
        Log.info('Summary favorite removed');
      } else {
        addMenuFavorite({
          url: GLOB.location.pathname + GLOB.location.search,
          label: result?.data?.data ? result.data.data.label : MenuNode.getParentNodes(GLOB.selectedItem).map((mn) => mn.titleText || mn.title).join(' > '),
          user_id: GLOB.userInfo?.user_id || 0,
        });
        setIsFavorite(true);
        Log.info('Summary favorite added');
      }
    } catch (error) {
      Log.error('Error adding summary favorite', error);
    }
    finally {
      setIsLoadingFavorites(false);
    }
  };

  useEffect(() => {
    if (isFavorite) {
      const isAlreadyFavorite = menuFavorites.some(mf => mf.url === GLOB.location.pathname + GLOB.location.search);
      if (!isAlreadyFavorite) {
        setIsFavorite(false);
      }
    }
  }, [menuFavorites]);

  const items: ItemType[] = [
    ...MenuNode.getParentNodes(GLOB.selectedItem).map((mn, index, array) => ({
      key: mn.url || mn.key,
      title: index === array.length - 1 ? <a title="Scroll this item into left menu view" onClick={GLOB.scrollSelectedItemIntoView}>{mn.titleText || mn.title}</a>
        : mn.titleText || mn.title
    })),
    ...(props.isSummary ? [{
      key: 'summary',
      title: <Tooltip title="Adds this summary page to your favorites for easy access in the top right corner.">
        <Button disabled={isLoadingFavorites} onClick={addSummaryFavorite} data-testid="favorite-btn" aria-pressed={isFavorite}>{
          isFavorite
            ? <BsBookmarkStarFill size={"0.8rem"} />
            : <BsBookmarkStar size={"0.8rem"} style={{ strokeWidth: 0.3 }} />
        }</Button></Tooltip>
    }] : []),
    ...(itemId && customLinks ? [{
      key: 'customLinks',
      title: <Tooltip title="Manage custom links for this item.">
        <Button data-testid="custom-links-btn" disabled={isLoadingCustomLinks} onClick={() => setIsCustomLinksModalOpen(true)}>
          <BsLink45Deg size={"1.1rem"} />
        </Button>
      </Tooltip>
    }] : [])
  ];

  return <div ref={containerRef} className={`${BREADCRUMB_CLASS} ${BREADCRUMB_CLASS}-center`} style={{ flexWrap: 'wrap' }}>
    <Breadcrumb separator="|" {...props.bread} className={` ${props.bread?.className || ''}`} items={items} />
    {GLOB.selectedClass === RoutePath.CUSTOM_GROUPS && (GLOB.location.hash === tabHeatmap || GLOB.location.hash === tabSubHeatmap) && <XmCgHeatmapFavorite className="xm-bread-side star" />}
    {GLOB.selectedClass === RoutePath.DASHBOARD && GLOB.selectedItem.parent?.itemId === HEATMAP_FOLDER_ITEM_ID
      && <Link className="hover-info xm-bread-side" title="Jump to related page"
        to={`${RoutePath.CUSTOM_GROUPS}/${GLOB.selectedItem.itemId}`}><FaShare /></Link>}
    {customLinks?.length > 0 && <>
      <div ref={linksMeasureRef} className="xm-bread-links xm-bread-links-measure" aria-hidden>
        {customLinks.map(link => <CustomLink key={link.id} {...link} />)}
      </div>
      <div className="xm-bread-links">
        {areLinksCollapsed
          ? <Dropdown trigger={['click', 'hover']} placement="bottomRight"
            popupRender={() => <div className="xm-bread-links-dropdown">
              {customLinks.map(link => <CustomLink key={link.id} {...link} />)}
            </div>}>
            <Button className="xm-bread-links-toggle" size="small"><BsLink45Deg size={"1.1rem"} />{customLinks.length}</Button>
          </Dropdown>
          : customLinks.map(link => <CustomLink key={link.id} {...link} />)}
      </div>
    </>}
    {itemId && customLinks && <CustomLinksModal
      key={`${customLinks.length}`}
      open={isCustomLinksModalOpen}
      itemId={itemId}
      links={customLinks}
      onCancel={() => setIsCustomLinksModalOpen(false)}
      onSaved={links => { setCustomLinks(links); setIsCustomLinksModalOpen(false); }}
    />}
  </div>;
};

interface CustomLinksFormData {
  custom_links: CustomLinkEntry[];
}

const CustomLinksModal = ({ open, itemId, links, onCancel, onSaved }: {
  open: boolean;
  itemId: string;
  links: ItemCustomLinksByItem['custom_links'];
  onCancel: () => void;
  onSaved: (links: ItemCustomLinksByItem['custom_links']) => void;
}) => {
  const [form] = Form.useForm<CustomLinksFormData>();

  if (!open)
    return null;

  const initialValues: Partial<CustomLinksFormData> = links.length
    ? { custom_links: links.map(({ label, url, color, newTab }) => ({ label, url, color, newTab })) }
    : {};

  const handleFinish = ({ custom_links: links }: CustomLinksFormData) => {
    const formData = links.map((link): UpdateCustomLinkDTO => ({ ...link, newTab: true, id: '' }));
    putApi<CustomLinksFlatResponseDTO, UpdateCustomLinkDTO[]>(`${API_URL.CONFIGURATION}/custom-links/${itemId}`, formData).then(
      ({ data: { data } }) => {
        onSaved(data.map(({ id, label, url, color, newTab }) => ({ id, label, url, color, newTab })));
        Log.info('Custom links saved');
        form.resetFields();
      },
      (reason: AxiosError) => Log.error('Failed to save custom links', reason)
    );
  };

  return (
    <Modal
      title="Manage custom links"
      open={open}
      onOk={() => form.submit()}
      onCancel={() => { form.resetFields(); onCancel(); }}
      destroyOnHidden
    >
      <Form<CustomLinksFormData>
        className="xm-custom-link-form"
        form={form}
        onFinish={handleFinish}
        initialValues={initialValues}
        style={{ marginTop: '1em' }}
      >
        <Card>
          <CustomLinksFormList required={false} />
        </Card>
      </Form>
    </Modal>
  );
};
